import { useEffect, useState } from 'react';

/**
 * Hook for interaction with the browsers local storage. By using this hook
 * a rerender (just like with useState) is triggered when updating a value.
 *
 * Local storage only supports string values therefore if other values are
 * needed they have to be cast to string beforehand. The exception to this
 * are objects and arrays which are supported through this hook.
 *
 * @param storageKey The key required for local storage to save and retrieve
 * a value. If a key is reused the value in local storage will be overwritten.
 *
 * @param defaultValue Default value for local storage on initialization.
 */
const useLocalStorage = <T extends string | object | null>(
  storageKey: string,
  defaultValue?: T,
): [T | null, (value?: T | null) => void] => {
  const clearLocalStorageItem = () => {
    localStorage.removeItem(storageKey);
    setValue(null);
  };

  const setLocalStorageItem = (newValue?: T | null) => {
    if (newValue === null) {
      clearLocalStorageItem();
      return;
    }

    switch (typeof newValue) {
      case 'string': {
        localStorage.setItem(storageKey, String(newValue));
        break;
      }
      case 'object': {
        localStorage.setItem(storageKey, JSON.stringify(newValue));
        break;
      }
      default: {
        clearLocalStorageItem();
        return;
      }
    }
    setValue(newValue);
  };

  const getLocalStorageItem = (): T | null => {
    const value = localStorage.getItem(storageKey);
    // Remove possible null and other json parsable values by
    // making sure local storage really returns a string value
    if (typeof value !== 'string') return null;

    try {
      return JSON.parse(value);
    } catch (_) {
      return value as T;
    }
  };

  const [value, setValue] = useState<T | null>(getLocalStorageItem());

  useEffect(() => {
    // Set default value on component mount if local storage is empty
    if (defaultValue && !value) {
      setLocalStorageItem(defaultValue);
    }
  }, []);

  return [value, setLocalStorageItem];
};

export default useLocalStorage;
